import BaseService from './base.service';

/**
 * Service for managing quiz collections
 */
class QuizCollectionService extends BaseService {
  /**
   * Get quiz collections with pagination and filters
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} params.search - Search term
   * @param {string} params.privacy - Privacy filter
   * @param {Array} params.tags - Tags filter
   * @returns {Promise<Object>} Quiz collections data with pagination
   */
  async getQuizCollections(params = {}) {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.privacy) queryParams.append('privacy', params.privacy);
    if (params.tags && params.tags.length > 0) {
      queryParams.append('tags', params.tags.join(','));
    }

    return this.get(`/quiz-collections?${queryParams.toString()}`);
  }

  /**
   * Get a specific quiz collection by ID
   * @param {number} id - Quiz collection ID
   * @returns {Promise<Object>} Quiz collection data
   */
  async getQuizCollectionById(id) {
    return this.get(`/quiz-collections/${id}`);
  }

  /**
   * Create a new quiz collection
   * @param {Object} data - Quiz collection data
   * @returns {Promise<Object>} Created quiz collection
   */
  async createQuizCollection(data) {
    return this.post('/quiz-collections', data);
  }

  /**
   * Update an existing quiz collection
   * @param {number} id - Quiz collection ID
   * @param {Object} data - Updated quiz collection data
   * @returns {Promise<Object>} Updated quiz collection
   */
  async updateQuizCollection(id, data) {
    return this.put(`/quiz-collections/${id}`, data);
  }

  /**
   * Delete a quiz collection
   * @param {number} id - Quiz collection ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteQuizCollection(id) {
    return this.delete(`/quiz-collections/${id}`);
  }

  /**
   * Get all available tags for quiz collections
   * @returns {Promise<Array>} Array of tag strings
   */
  async getAllTags() {
    return this.get('/quiz-collections/tags');
  }

  /**
   * Get observations attached to a quiz collection
   * @param {number} id - Quiz collection ID
   * @returns {Promise<Array>} Array of observations with scores
   */
  async getQuizCollectionObservations(id) {
    return this.get(`/quiz-collections/${id}/observations`);
  }

  /**
   * Send quiz to technologists via email
   * @param {number} quizId - Quiz collection ID
   * @param {Object} data - Request data
   * @param {Array} data.technologistIds - Array of technologist IDs
   * @param {string} [data.message] - Optional message to include
   * @returns {Promise<Object>} Response with success status and sent count
   */
  async sendToTechnologists(quizId, data) {
    return this.post(`/quiz-collections/${quizId}/send-to-technologists`, data);
  }

  /**
   * Get evaluation data by observation ID
   * @param {number} observationId - Observation ID
   * @returns {Promise<Object>} Evaluation data with questions and answers
   */
  async getEvaluationByObservationId(observationId) {
    return this.get(`/quality-evaluation/observation/${observationId}`);
  }

  /**
   * Get materials attached to a quiz collection
   * @param {number} id - Quiz collection ID
   * @returns {Promise<Array>} Array of materials
   */
  async getQuizCollectionMaterials(id) {
    return this.get(`/quiz-collections/${id}/materials`);
  }

  /**
   * Attach materials to a quiz collection
   * @param {number} id - Quiz collection ID
   * @param {Array<number>} materialIds - Array of material IDs to attach
   * @returns {Promise<Object>} Response with success status
   */
  async attachMaterials(id, materialIds) {
    return this.post(`/quiz-collections/${id}/materials`, { materialIds });
  }

  /**
   * Detach a material from a quiz collection
   * @param {number} id - Quiz collection ID
   * @param {number} materialId - Material ID to detach
   * @returns {Promise<Object>} Response with success status
   */
  async detachMaterial(id, materialId) {
    return this.delete(`/quiz-collections/${id}/materials/${materialId}`);
  }

  /**
   * Update material order for a quiz collection
   * @param {number} id - Quiz collection ID
   * @param {Array<number>} materialIds - Array of material IDs in new order
   * @returns {Promise<Object>} Response with success status
   */
  async updateMaterialOrder(id, materialIds) {
    return this.put(`/quiz-collections/${id}/materials/order`, { materialIds });
  }

  // Quiz Access and Invitation Methods

  /**
   * Check technologist access to a quiz collection
   * @param {number} quizId - Quiz collection ID
   * @param {number} technologistId - Technologist user ID
   * @returns {Promise<Object>} Access information with mode and invitation details
   */
  async checkTechnologistAccess(quizId, technologistId) {
    return this.get(`/quiz-collections/${quizId}/access/${technologistId}`);
  }

  /**
   * Get quiz invitations for a technologist
   * @param {number} technologistId - Technologist user ID
   * @param {string} [status] - Optional status filter (pending, started, completed, expired)
   * @returns {Promise<Object>} Invitations data
   */
  async getTechnologistInvitations(technologistId, status = null) {
    const queryParams = status ? `?status=${status}` : '';
    return this.get(`/quiz-collections/invitations/${technologistId}${queryParams}`);
  }

  /**
   * Create a quiz invitation for a technologist
   * @param {number} quizId - Quiz collection ID
   * @param {number} technologistId - Technologist user ID
   * @returns {Promise<Object>} Created invitation
   */
  async createQuizInvitation(quizId, technologistId) {
    return this.post(`/quiz-collections/${quizId}/invitations`, { technologistId });
  }

  // Quiz Progress Methods

  /**
   * Get quiz progress for a technologist
   * @param {number} quizId - Quiz collection ID
   * @param {number} technologistId - Technologist user ID
   * @returns {Promise<Object>} Progress data or null if not started
   */
  async getTechnologistProgress(quizId, technologistId) {
    return this.get(`/quiz-collections/${quizId}/progress/${technologistId}`);
  }

  /**
   * Start quiz progress for a technologist
   * @param {number} quizId - Quiz collection ID
   * @param {number} technologistId - Technologist user ID
   * @returns {Promise<Object>} Started progress data
   */
  async startQuizProgress(quizId, technologistId) {
    return this.post(`/quiz-collections/${quizId}/progress/${technologistId}/start`);
  }

  /**
   * Update quiz progress for a technologist
   * @param {number} quizId - Quiz collection ID
   * @param {number} technologistId - Technologist user ID
   * @param {Object} updateData - Progress update data
   * @param {number} [updateData.currentObservationIndex] - Current observation index
   * @param {Array<number>} [updateData.completedObservations] - Array of completed observation IDs
   * @param {Object} [updateData.observationProgress] - Observation-specific progress data
   * @returns {Promise<Object>} Updated progress data
   */
  async updateQuizProgress(quizId, technologistId, updateData) {
    return this.put(`/quiz-collections/${quizId}/progress/${technologistId}`, updateData);
  }

  /**
   * Mark an observation as completed in quiz progress
   * @param {number} quizId - Quiz collection ID
   * @param {number} technologistId - Technologist user ID
   * @param {number} observationId - Observation ID to mark as completed
   * @param {Object} observationData - Observation completion data (video progress, evaluation answers, etc.)
   * @returns {Promise<Object>} Updated progress data
   */
  async completeObservation(quizId, technologistId, observationId, observationData) {
    return this.post(`/quiz-collections/${quizId}/progress/${technologistId}/observations/${observationId}/complete`, observationData);
  }

  /**
   * Mark the entire quiz as completed for a technologist
   * @param {number} quizId - Quiz collection ID
   * @param {number} technologistId - Technologist user ID
   * @returns {Promise<Object>} Completed progress data
   */
  async completeQuiz(quizId, technologistId) {
    return this.post(`/quiz-collections/${quizId}/progress/${technologistId}/complete`);
  }
}

export default QuizCollectionService;
